import { useState, useEffect, useCallback } from 'react'
import apiClient from '@/lib/api'
import { isAxiosError } from 'axios'
import {
  Button,
  Card,
  EmptyState,
  LoadingState,
  ErrorState,
  StatusBadge,
  IconButton,
} from '@/components/ui'
import {
  Building2,
  RefreshCw,
  Search,
  Unlink,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface HrRecord {
  id: string
  bpId: string
  openregisterCompanyId: string | null
  status: 'active' | 'inactive' | 'liquidation' | 'unknown'
  registerNumber: string | null
  registerType: string | null
  registerCourt: string | null
  legalForm: string | null
  managementJson: Array<{ name: string; role: string; dismissed?: boolean }> | null
  addressJson: { street?: string; zip?: string; city?: string } | null
  financialJson: {
    revenue?: number
    net_income?: number
    employee_count?: number
    year?: number
  } | null
  fullDataJson: Record<string, unknown> | null
  linkedAt: string | null
  detailCachedAt: string | null
  lastChangeDetectedAt: string | null
}

interface SearchCandidate {
  company_id: string
  name: string
  register_number: string
  register_type: string
  register_court: string
  legal_form: string
  active: boolean
}

interface CreditStatus {
  available: boolean
  currentMonth: {
    month: string
    creditsUsed: number
    monthlyBudget: number
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '–'
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatCurrency(val: number | undefined): string {
  if (val == null) return '–'
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val)
}

function StatusIcon({ status }: { status: HrRecord['status'] }) {
  if (status === 'active') return <CheckCircle2 className="w-4 h-4 text-green-500" />
  if (status === 'liquidation') return <AlertTriangle className="w-4 h-4 text-orange-500" />
  if (status === 'inactive') return <XCircle className="w-4 h-4 text-red-500" />
  return <Clock className="w-4 h-4 text-gray-400" />
}

function statusLabel(status: HrRecord['status']): string {
  const map: Record<HrRecord['status'], string> = {
    active: 'Aktiv',
    inactive: 'Inaktiv',
    liquidation: 'In Liquidation',
    unknown: 'Unbekannt',
  }
  return map[status] ?? status
}

function statusBadgeStatus(status: HrRecord['status']): string {
  if (status === 'active') return 'active'
  if (status === 'liquidation') return 'warning'
  if (status === 'inactive') return 'inactive'
  return 'unknown'
}

// ─── Search Modal ─────────────────────────────────────────────────────────────

function SearchPanel({
  bpId,
  bpName,
  onLinked,
  onClose,
}: {
  bpId: string
  bpName: string
  onLinked: () => void
  onClose: () => void
}) {
  const [query, setQuery] = useState(bpName)
  const [results, setResults] = useState<SearchCandidate[]>([])
  const [searching, setSearching] = useState(false)
  const [linking, setLinking] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const doSearch = async () => {
    if (!query.trim()) return
    setSearching(true)
    setError(null)
    try {
      const res = await apiClient.post(`/handelsregister/bp/${bpId}/search`, { query })
      setResults(res.data.data ?? [])
    } catch (err: unknown) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.error ?? err.response?.data?.message ?? err.message)
        : String(err)
      setError(msg)
    } finally {
      setSearching(false)
    }
  }

  const doLink = async (candidate: SearchCandidate) => {
    setLinking(candidate.company_id)
    setError(null)
    try {
      await apiClient.post(`/handelsregister/bp/${bpId}/link`, { company: candidate })
      setLinking(null)
      onLinked()
    } catch (err: unknown) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.error ?? err.response?.data?.message ?? err.message)
        : String(err)
      setError(msg)
      setLinking(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          className="flex-1 rounded border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          placeholder="Firmenname suchen…"
        />
        <Button variant="primary" size="sm" onClick={doSearch} disabled={searching}>
          {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Suchen
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>Abbrechen</Button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {results.length > 0 && (
        <div className="divide-y divide-neutral-100 border border-neutral-200 rounded-md overflow-hidden">
          {results.map((r) => (
            <div key={r.company_id} className="flex items-start justify-between gap-4 px-4 py-3 bg-white hover:bg-neutral-50">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate">{r.name}</span>
                  {!r.active && (
                    <span className="text-xs bg-red-100 text-red-700 rounded px-1.5 py-0.5">Nicht aktiv</span>
                  )}
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {[r.register_type, r.register_number, r.register_court, r.legal_form].filter(Boolean).join(' · ')}
                </div>
              </div>
              <Button
                variant="secondary"
                size="xs"
                onClick={() => doLink(r)}
                disabled={linking === r.company_id}
              >
                {linking === r.company_id ? <RefreshCw className="w-3 h-3 animate-spin" /> : null}
                Verknüpfen
              </Button>
            </div>
          ))}
        </div>
      )}

      {results.length === 0 && !searching && !error && (
        <p className="text-sm text-neutral-500 text-center py-2">
          Suche starten, um Treffer anzuzeigen (kostet 1 Credit).
        </p>
      )}
    </div>
  )
}

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export function HandelsregisterTab({ bpId, bpName, sapRegistrationNumber }: { bpId: string; bpName: string; sapRegistrationNumber?: string | null }) {
  const [record, setRecord] = useState<HrRecord | null>(null)
  const [credits, setCredits] = useState<CreditStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [unlinking, setUnlinking] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [showRawData, setShowRawData] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [recRes, credRes] = await Promise.all([
        apiClient.get(`/handelsregister/bp/${bpId}`),
        apiClient.get('/handelsregister/status').catch(() => ({ data: null })),
      ])
      setRecord(recRes.data.data ?? null)
      if (credRes.data) setCredits(credRes.data)
    } catch (err: unknown) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.error ?? err.response?.data?.message ?? err.message)
        : String(err)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [bpId])

  useEffect(() => { void load() }, [load])

  const doRefresh = async () => {
    setRefreshing(true)
    setError(null)
    try {
      const res = await apiClient.post(`/handelsregister/bp/${bpId}/refresh`, {})
      setRecord(res.data.data ?? null)
      // reload credits
      const credRes = await apiClient.get('/handelsregister/status')
      setCredits(credRes.data)
    } catch (err: unknown) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.error ?? err.response?.data?.message ?? err.message)
        : String(err)
      setError(msg)
    } finally {
      setRefreshing(false)
    }
  }

  const doUnlink = async () => {
    if (!confirm('Verknüpfung und gecachte Daten wirklich löschen?')) return
    setUnlinking(true)
    try {
      await apiClient.delete(`/handelsregister/bp/${bpId}`)
      setRecord(null)
    } catch (err: unknown) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.error ?? err.response?.data?.message ?? err.message)
        : String(err)
      setError(msg)
    } finally {
      setUnlinking(false)
    }
  }

  if (loading) return <LoadingState label="Handelsregisterdaten laden…" />

  // Not configured
  if (credits && !credits.available) {
    return (
      <EmptyState
        icon={<Building2 />}
        title="OpenRegister nicht konfiguriert"
        description="Hinterlege einen API-Key unter Einstellungen → Integrationen → OpenRegister, um Handelsregisterdaten abzurufen."
      />
    )
  }

  return (
    <div className="space-y-4">
      {/* Credit Budget Bar */}
      {credits && (
        <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-2.5">
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <span className="font-medium">Credits {credits.currentMonth.month}:</span>
            <span className={credits.currentMonth.creditsUsed >= credits.currentMonth.monthlyBudget ? 'text-red-600 font-semibold' : ''}>
              {credits.currentMonth.creditsUsed} / {credits.currentMonth.monthlyBudget}
            </span>
          </div>
          <div className="w-40 h-1.5 rounded-full bg-neutral-200 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                credits.currentMonth.creditsUsed / credits.currentMonth.monthlyBudget > 0.8
                  ? 'bg-red-500'
                  : 'bg-primary-500'
              }`}
              style={{ width: `${Math.min(100, (credits.currentMonth.creditsUsed / credits.currentMonth.monthlyBudget) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {error && (
        <ErrorState
          title="Fehler"
          description={error}
          onRetry={load}
        />
      )}

      {/* Search Panel */}
      {showSearch && (
        <Card>
          <Card.Header>
            <Card.Title>Unternehmen suchen &amp; verknüpfen</Card.Title>
          </Card.Header>
          <Card.Body>
            <SearchPanel
              bpId={bpId}
              bpName={sapRegistrationNumber || bpName}
              onLinked={() => { setShowSearch(false); void load() }}
              onClose={() => setShowSearch(false)}
            />
          </Card.Body>
        </Card>
      )}

      {/* No link yet */}
      {!record?.openregisterCompanyId && !showSearch && (
        <EmptyState
          icon={<Building2 />}
          title="Noch nicht verknüpft"
          description="Verknüpfe diesen Geschäftspartner mit einem Handelsregistereintrag, um Status, Geschäftsführer und Finanzkennzahlen zu tracken."
          action={
            <Button variant="primary" onClick={() => setShowSearch(true)}>
              <Search className="w-4 h-4" />
              Unternehmen suchen
            </Button>
          }
        />
      )}

      {/* Linked record */}
      {record?.openregisterCompanyId && (
        <>
          {/* Header card with status */}
          <Card>
            <Card.Body>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <StatusIcon status={record.status} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={statusBadgeStatus(record.status)} label={statusLabel(record.status)} />
                      {record.legalForm && (
                        <span className="text-xs text-neutral-500 bg-neutral-100 rounded px-1.5 py-0.5">{record.legalForm}</span>
                      )}
                    </div>
                    <div className="mt-1.5 text-sm text-neutral-600 space-y-0.5">
                      {record.registerNumber && (
                        <div>
                          <span className="text-neutral-400 mr-1">Register:</span>
                          {[record.registerType, record.registerNumber].filter(Boolean).join(' ')}
                          {record.registerCourt && ` · ${record.registerCourt}`}
                        </div>
                      )}
                      {record.addressJson && (
                        <div>
                          <span className="text-neutral-400 mr-1">Adresse:</span>
                          {[record.addressJson.street, `${record.addressJson.zip ?? ''} ${record.addressJson.city ?? ''}`.trim()].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-neutral-400 space-x-3">
                      {record.detailCachedAt && <span>Details: {formatDate(record.detailCachedAt)}</span>}
                      {record.linkedAt && <span>Verknüpft: {formatDate(record.linkedAt)}</span>}
                      {record.lastChangeDetectedAt && (
                        <span className="text-orange-500 font-medium">Änderung erkannt: {formatDate(record.lastChangeDetectedAt)}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={doRefresh}
                    disabled={refreshing}
                    title="Vollständige Details abrufen (10 Credits)"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    {record.detailCachedAt ? 'Aktualisieren' : 'Details laden'} (10 Credits)
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowSearch(true)} title="Andere Verknüpfung wählen">
                    <Search className="w-4 h-4" />
                  </Button>
                  <IconButton
                    icon={<Unlink />}
                    aria-label="Verknüpfung aufheben"
                    variant="ghost"
                    size="sm"
                    onClick={doUnlink}
                    disabled={unlinking}
                  />
                  <a
                    href={`https://openregister.de/company/${record.openregisterCompanyId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 transition-colors"
                    title="Auf openregister.de öffnen"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Management */}
          {record.managementJson && record.managementJson.length > 0 && (
            <Card>
              <Card.Header>
                <Card.Title>Geschäftsführung / Management</Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="divide-y divide-neutral-100">
                  {record.managementJson.map((person, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                      <div>
                        <span className={`font-medium text-sm ${person.dismissed ? 'line-through text-neutral-400' : ''}`}>
                          {person.name}
                        </span>
                        <span className="ml-2 text-xs text-neutral-500">{person.role}</span>
                      </div>
                      {person.dismissed && (
                        <span className="text-xs bg-neutral-100 text-neutral-500 rounded px-1.5 py-0.5">Abberufen</span>
                      )}
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Financials */}
          {record.financialJson && (
            <Card>
              <Card.Header>
                <Card.Title>
                  Finanzkennzahlen
                  {record.financialJson.year && (
                    <span className="ml-2 text-xs font-normal text-neutral-400">({record.financialJson.year})</span>
                  )}
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {record.financialJson.revenue != null && (
                    <div>
                      <div className="text-xs text-neutral-500">Umsatz</div>
                      <div className="font-semibold text-sm mt-0.5">{formatCurrency(record.financialJson.revenue)}</div>
                    </div>
                  )}
                  {record.financialJson.net_income != null && (
                    <div>
                      <div className="text-xs text-neutral-500">Jahresüberschuss</div>
                      <div className={`font-semibold text-sm mt-0.5 ${record.financialJson.net_income < 0 ? 'text-red-600' : ''}`}>
                        {formatCurrency(record.financialJson.net_income)}
                      </div>
                    </div>
                  )}
                  {record.financialJson.employee_count != null && (
                    <div>
                      <div className="text-xs text-neutral-500">Mitarbeiter</div>
                      <div className="font-semibold text-sm mt-0.5">{record.financialJson.employee_count.toLocaleString('de-DE')}</div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}

          {/* No detail data yet */}
          {!record.detailCachedAt && (
            <div className="rounded-lg border border-dashed border-neutral-300 p-6 text-center">
              <p className="text-sm text-neutral-500">
                Nur Basisdaten aus der Suche vorhanden. Klicke auf <strong>Details laden</strong>, um vollständige Handelsregisterdaten abzurufen (10 Credits).
              </p>
            </div>
          )}

          {/* Raw Data Accordion */}
          {record.fullDataJson && (
            <div className="rounded-lg border border-neutral-200 overflow-hidden">
              <button
                className="flex w-full items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors text-sm font-medium"
                onClick={() => setShowRawData((v) => !v)}
              >
                <span className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-neutral-400" />
                  Rohdaten (API-Response)
                </span>
                {showRawData ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showRawData && (
                <pre className="p-4 text-xs bg-white overflow-auto max-h-96 text-neutral-700">
                  {JSON.stringify(record.fullDataJson, null, 2)}
                </pre>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
